import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../services/apiClient';
import { AppConfig, AvailableHostPool, HostPoolConfig } from '../types/appConfig';
import { buildRuntimeAppConfig } from '../services/runtimeConfig';

function toMultilineValue(values: string[]): string {
  return values.join('\n');
}

function fromMultilineValue(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/\r?\n|,/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

function hostPoolKey(hostPool: HostPoolConfig): string {
  return hostPool.id.toLowerCase();
}

export default function AdminSettingsPage() {
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const [config, setConfig] = useState<AppConfig>(buildRuntimeAppConfig());
  const [availableHostPools, setAvailableHostPools] = useState<AvailableHostPool[]>([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadConfig = async () => {
    const response = await apiClientRef.current.getAdminConfig();
    setConfig(response);
    setAvailableHostPools((current) =>
      current.map((hostPool) => ({
        ...hostPool,
        selected: response.hostPools.some((selected) => hostPoolKey(selected) === hostPoolKey(hostPool)),
      }))
    );
  };

  useEffect(() => {
    loadConfig()
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Administration konnte nicht geladen werden.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const updateBackendField = (field: keyof AppConfig['backend'], value: string) => {
    setConfig((current) => ({
      ...current,
      backend: {
        ...current.backend,
        [field]: value,
      },
      frontend: field === 'azureTenantId'
        ? {
            ...current.frontend,
            azureTenantId: value,
          }
        : current.frontend,
    }));
  };

  const handleInput =
    (handler: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handler(event.target.value);
    };

  const loadAvailableHostPools = async () => {
    try {
      setDiscovering(true);
      setError(null);
      setSuccessMessage(null);
      await apiClientRef.current.updateAdminConfig(config);
      const response = await apiClientRef.current.getAvailableHostPools();
      setAvailableHostPools(response.hostPools);
      setConfig((current) => ({
        ...current,
        hostPools: response.hostPools
          .filter((hostPool) => hostPool.selected)
          .map((hostPool) => ({
            id: hostPool.id,
            name: hostPool.name,
            resourceGroup: hostPool.resourceGroup,
          })),
      }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Host Pools konnten nicht geladen werden.');
    } finally {
      setDiscovering(false);
    }
  };

  const toggleHostPool = (hostPool: AvailableHostPool) => {
    setAvailableHostPools((current) =>
      current.map((entry) =>
        hostPoolKey(entry) === hostPoolKey(hostPool)
          ? {
              ...entry,
              selected: !entry.selected,
            }
          : entry
      )
    );

    setConfig((current) => {
      const isSelected = current.hostPools.some((entry) => hostPoolKey(entry) === hostPoolKey(hostPool));
      return {
        ...current,
        hostPools: isSelected
          ? current.hostPools.filter((entry) => hostPoolKey(entry) !== hostPoolKey(hostPool))
          : [
              ...current.hostPools,
              {
                id: hostPool.id,
                name: hostPool.name,
                resourceGroup: hostPool.resourceGroup,
              },
            ],
      };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      const savedConfig = await apiClientRef.current.updateAdminConfig(config);
      setConfig(savedConfig);
      setSuccessMessage('Einstellungen gespeichert.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="panel py-12 text-center">
        <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-2 border-line border-b-red"></div>
        <p className="text-muted">Administration wird geladen...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow">Administration</p>
          <h2 className="mt-2 text-3xl font-semibold text-ink">Host Pools</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={loadAvailableHostPools} disabled={discovering}>
            {discovering ? 'Lade...' : 'Aus Azure laden'}
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichere...' : 'Speichern'}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice-error">
          <p className="font-medium">Aktion fehlgeschlagen</p>
          <p className="mt-1 text-sm">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="notice-success">
          <p className="font-medium">{successMessage}</p>
          {config.metadata?.updatedAt && (
            <p className="mt-1 text-sm">
              {new Date(config.metadata.updatedAt).toLocaleString()} · {config.metadata.updatedBy}
            </p>
          )}
        </div>
      )}

      <section className="panel p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="field-label">Tenant-ID</span>
            <input
              className="field-input"
              value={config.backend.azureTenantId}
              onChange={handleInput((value) => updateBackendField('azureTenantId', value))}
            />
          </label>
          <label className="block">
            <span className="field-label">Subscription-ID</span>
            <input
              className="field-input"
              value={config.backend.azureSubscriptionId}
              onChange={handleInput((value) => updateBackendField('azureSubscriptionId', value))}
            />
          </label>
          <label className="block">
            <span className="field-label">App Client-ID</span>
            <input
              className="field-input"
              value={config.backend.azureClientId}
              onChange={handleInput((value) => updateBackendField('azureClientId', value))}
            />
          </label>
          <label className="block">
            <span className="field-label">API Basis-URL</span>
            <input
              className="field-input"
              value={config.frontend.apiBaseUrl}
              onChange={handleInput((value) =>
                setConfig((current) => ({
                  ...current,
                  frontend: {
                    ...current.frontend,
                    apiBaseUrl: value,
                  },
                }))
              )}
            />
          </label>
          <label className="block md:col-span-2">
            <span className="field-label">Resource Groups</span>
            <textarea
              className="field-textarea"
              value={toMultilineValue(config.resourceGroups)}
              onChange={handleInput((value) =>
                setConfig((current) => ({
                  ...current,
                  resourceGroups: fromMultilineValue(value),
                }))
              )}
            />
          </label>
        </div>
      </section>

      <section className="panel overflow-hidden">
        <div className="border-b border-line px-6 py-4">
          <p className="text-sm font-semibold text-ink">
            {config.hostPools.length} Host Pool{config.hostPools.length === 1 ? '' : 's'} aktiv
          </p>
        </div>

        {availableHostPools.length === 0 ? (
          <div className="px-6 py-10 text-sm text-muted">Noch keine Host Pools geladen.</div>
        ) : (
          <div className="divide-y divide-line">
            {availableHostPools.map((hostPool) => (
              <label
                key={hostPool.id}
                className="flex cursor-pointer items-center justify-between gap-4 px-6 py-4 hover:bg-soft"
              >
                <span>
                  <span className="block font-medium text-ink">{hostPool.name}</span>
                  <span className="block text-sm text-muted">{hostPool.resourceGroup}</span>
                </span>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-red"
                  checked={config.hostPools.some((entry) => hostPoolKey(entry) === hostPoolKey(hostPool))}
                  onChange={() => toggleHostPool(hostPool)}
                />
              </label>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
